import { useEffect, useState } from 'react';
import type { ImageNaturalSize } from './types';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useImageLoader');

interface UseImageLoaderProps {
  imageUrl: string;
}

interface UseImageLoaderReturn {
  imageLoaded: boolean;
  imageError: string | null;
  imageNaturalSize: ImageNaturalSize;
}

export function useImageLoader({
  imageUrl,
}: UseImageLoaderProps): UseImageLoaderReturn {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageNaturalSize, setImageNaturalSize] = useState<ImageNaturalSize>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!imageUrl) {
      console.error('[useImageLoader] No image URL provided');
      setImageError('No image URL provided');
      return;
    }

    logger.info('Loading image from URL:', imageUrl.substring(0, 50) + '...');

    const img = new Image();
    const loadStartTime = performance.now();

    img.onload = () => {
      const loadTime = performance.now() - loadStartTime;

      // Use naturalWidth/Height (not width/height) so the size matches the
      // EXIF-oriented dimensions the browser also applies to the displayed
      // <img>. Mixing the two transposes the scale for rotated phone photos
      // and drifts the crop frame off-screen.
      setImageNaturalSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      setImageLoaded(true);
      setImageError(null);

      if (loadTime > 3000) {
        console.warn(
          '[useImageLoader] Image loading took longer than 3s - possible mobile performance issue'
        );
      }
    };

    img.onerror = (e) => {
      const loadTime = performance.now() - loadStartTime;

      console.error('[useImageLoader] Failed to load image:', {
        error: e,
        imageUrl: imageUrl.substring(0, 100),
        loadTimeMs: loadTime.toFixed(0),
        errorType:
          typeof e === 'object' && e && 'type' in e ? e.type : 'unknown',
      });

      setImageError(
        'Failed to load image. The file may be corrupted or your device may be out of memory.'
      );
      setImageLoaded(false);
    };

    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  return {
    imageLoaded,
    imageError,
    imageNaturalSize,
  };
}
